import type {Database} from '$lib/classes/interfaces/database';
import type {UserWordConverter} from '$lib/classes/data/user-word-converter';
import type {UserWordJson} from '$lib/classes/data/user-word-json';
import type {RemoteChanges} from '$lib/classes/stores/remote-changes.svelte';
import type {WordIdToWord} from '$lib/data/wordIdToWord';
import {SentenceManager} from '$lib/classes/manager/sentence-manager';
import type {WordIdChanges} from '$lib/data/word-id-changes';
import type {Interval} from '$lib/utils/userWords/interval';
import type {IntervalMs, SentenceId, UserId, WordId} from '$lib/classes/data/_primitives';
import {Status, UserWord} from '$lib/classes/data/user-word';
import {validateUserWords} from '$lib/utils/userWords/validate-user-words';
import {logInProduction} from '$lib/utils/logInProduction';
import {Rating} from '$lib/classes/_enum/rating';
import {Action} from '$lib/classes/_types/action';
import type {Functions} from '$lib/classes/services/functions';
import {ResetError, SubscriptionError} from '$lib/classes/errors';
import {Timer} from '$lib/classes/timer';

type UserWordsMap = Record<WordId, UserWord>

export class UserWordManager {
    constructor(
        private database: Database,
        private userWordConverter: UserWordConverter,
        private remoteChanges: RemoteChanges,
        private wordIdToWord: WordIdToWord,
        private sentenceManager: SentenceManager,
        private wordIdChanges: WordIdChanges,
        private interval: typeof Interval,
        private readonly functions: Functions
    ) {}

    private LOCAL_STORAGE_KEY = 'user-words-v1'
    private isLoadingPersistedUserWordsActivated = false

    private uid: UserId | null = null
    private cancelCollectionSubscriptionCallback = () => {}

    private state: UserWordsMap = $state({})

    private reset() {
        this.state = {}
    }

    private create(id: WordId, status: Status, sentenceId: SentenceId | null) {
        const now = Date.now()
        return new UserWord(id, status, sentenceId, now, now)
    }

    private add(userWord: UserWord) {
        this.state[userWord.id] = userWord
    }

    private remove(userWord: UserWord) {
        delete this.state[userWord.id]
    }

    private addAndSave(userWord: UserWord) {
        this.add(userWord)
        if (this.uid) {
            this.database.setUserWordsDoc(this.uid, userWord)
                .catch(error => {
                    // TODO proper error handling - where/how?
                    console.error('ERROR while saving UserWord to DB', error)
                })
        } else {
            this.persistUserWords()
        }
    }

    private removeAndSave(userWord: UserWord) {
        this.remove(userWord)
        if (this.uid) {
            this.database.deleteUserWord(this.uid, userWord)
                .catch(error => {
                    console.error('ERROR while deleting UserWord from DB', error)
                })
        } else {
            this.persistUserWords()
        }
    }

    private persistUserWords() {
        const userWords = Object.values(this.state)
        const userWordsJson = userWords.map(userWord => this.userWordConverter.toJson(userWord))
        try {
            localStorage.setItem(this.LOCAL_STORAGE_KEY, JSON.stringify(userWordsJson))
        } catch (error) {
            console.error('ERROR persisting userWords', error)
        }
    }

    private loadAndSavePersistedUserWords() {
        try {
            const userWordsStr = localStorage.getItem(this.LOCAL_STORAGE_KEY)
            if (userWordsStr) {
                const persistedUserWordsJson = (JSON.parse(userWordsStr) as UserWordJson[])
                const persistedUserWords = persistedUserWordsJson.map(userWordJson => this.userWordConverter.fromJson(userWordJson))
                const {
                    validated,
                    toSave,
                } = validateUserWords(persistedUserWords, this.wordIdToWord, this.sentenceManager, this.wordIdChanges)

                //also SAVE validated since not yet in DB and IGNORE toDelete
                validated.forEach(userWord => this.addAndSave(userWord))
                toSave.forEach(userWord => this.addAndSave(userWord))
            }
        } catch (error) {
            console.error('ERROR loading and saving persisted user words', error)
        }
    }

    private clearPersistedUserWords() {
        try {
            localStorage.removeItem(this.LOCAL_STORAGE_KEY);
        } catch (error) {
            console.error('ERROR clearing persisted user words', error)
        }
    }

    activateLoadPersistedUserWords() {
        this.isLoadingPersistedUserWordsActivated = true
    }

    private initialSnapshot = true // class field because modified in stopDbSubscription()

    private async startDbSubscription() {
        if (!this.uid) throw new Error('UserWordManager.startDocSubscription called without uid set')

        const {
            unsubscribe,
            firstValue
        } = this.database.subscribeUserWordsCollectionNew(this.uid, (userWordChanges) => {

            if (this.initialSnapshot) {
                // VALIDATE saved user words
                // QUESTION firestore subscription does serve from memory cache - is it save that
                //  initial snapshot callback always holds the latest data...?!?!

                this.initialSnapshot = false

                const userWords = userWordChanges.map((change) => this.userWordConverter.fromJson(change.data as UserWordJson))

                const {
                    validated,
                    toSave,
                    toDelete
                } = validateUserWords(userWords, this.wordIdToWord, this.sentenceManager, this.wordIdChanges)

                if (toSave.length > 0 || toDelete.length > 0) console.log('>>>', 'validated', validated, 'toSave', toSave, 'toDelete', toDelete)

                validated.forEach(userWord => this.add(userWord))
                toSave.forEach(userWord => this.addAndSave(userWord))
                toDelete.forEach(userWord => this.removeAndSave(userWord))

            } else {
                for (const change of userWordChanges) {
                    // NOTE use for-of loop to break out in case of remote changes (not possible with forEach)

                    //IGNORE local changes >> state updated optimistically in .saveUserWord() and .deleteUserWord()
                    if (change.changedLocally) continue

                    //CHANGES from remote client detected !!
                    // NOTE changedLocally always false with Action.Removed >> can't be included in remote changes check
                    if (!change.changedLocally && change.action !== Action.Removed) {
                        this.remoteChanges.detected()
                        break;
                    }

                    //SYNC deletions since it can't be tracked if they are local or remote
                    if (change.action === Action.Removed) {
                        this.remove(UserWord.fromJson(change.data as UserWordJson))
                    }
                }
            }
        })

        this.cancelCollectionSubscriptionCallback = unsubscribe
        await firstValue
    }

    private stopDbSubscription() {
        this.cancelCollectionSubscriptionCallback()
        this.cancelCollectionSubscriptionCallback = () => {}
        this.initialSnapshot = true
    }

    // PUBLIC API

    async init(userId: UserId) {
        const timer = new Timer('userWordsMap.init')
        this.uid = userId

        this.reset() //important!!! Words may have been added before when logged out

        try {
            await this.startDbSubscription()
        } catch (error) {
            throw new Error('ERROR userWordsManager.init - subscribeUserWordsCollection', {cause: error})
        }

        if (this.isLoadingPersistedUserWordsActivated) {
            this.isLoadingPersistedUserWordsActivated = false
            this.loadAndSavePersistedUserWords()
        }
        this.clearPersistedUserWords() //always clear since they won't be needed again

        timer.stop()
    }

    close() {
        this.stopDbSubscription()
        this.reset()
        this.uid = null
        this.clearPersistedUserWords() //will be called onLogout >> on every new unauthenticated session
    }

    async resetUserWords() {

        //TODO block subsequent calls while resetting until finished

        this.stopDbSubscription()
        this.reset() // clear local state regardless of errors

        // Reset words
        let resetError
        try {
            await this.functions.resetWords()
        } catch (error) {
            resetError = error
        }

        // Restart subscription - ERROR here more important than reset error
        try {
            await this.startDbSubscription()
        } catch (error) {
            throw new SubscriptionError('ERROR restarting user words subscription after reset', {cause: error})
        }

        // Throw reset error if any
        if (resetError) {
            throw new ResetError('ERROR resetting user words', {cause: resetError})
        }
    }

    getUserWord(id: WordId): UserWord | undefined {
        return this.state[id]
    }

    userWords = $derived(Object.values(this.state).sort((uW1, uW2) => uW1.added - uW2.added))

    statusChangeRequest(id: WordId, newStatus: Status | null) {

        const userWord = this.getUserWord(id)

        const deleteUserWord = userWord && newStatus === null
        const userWordToDeleteNotFound = !userWord && newStatus === null
        const changeStatus = userWord && newStatus !== null && newStatus !== userWord.status
        const addUserWord = !userWord && newStatus !== null

        if (deleteUserWord) {
            logInProduction('delete ' + id + ' - ' + new Date().toLocaleString() + ' - ' + Date.now())
            this.removeAndSave(userWord)

        } else if (userWordToDeleteNotFound) {
            console.error(`ERROR no user word found for id ${id}. Cannot delete.`)

        } else if (changeStatus) {
            logInProduction('toggle ' + id + ' - ' + new Date().toLocaleString() + ' - ' + Date.now())
            const withNewStatus = userWord.withStatus(newStatus)
            this.addAndSave(withNewStatus)

        } else if (addUserWord) {
            logInProduction('add ' + id + ' - ' + new Date().toLocaleString() + ' - ' + Date.now())
            const sentenceId = this.sentenceManager.getFirstSentence(id)?.id ?? null
            const newUserWord = this.create(id, newStatus, sentenceId)
            this.addAndSave(newUserWord)
        }
    }

    sentenceChangeRequest(id: WordId, newSentenceId: SentenceId) {

        const userWord = this.getUserWord(id)

        const addUserWord = !userWord
        const changeSentenceId = userWord && newSentenceId !== userWord.sentenceId

        if (addUserWord) {
            const newUserWord = this.create(id, Status.Learn, newSentenceId)
            this.addAndSave(newUserWord)

        } else if (changeSentenceId) {
            const withSentenceId = userWord.withSentenceId(newSentenceId)
            this.addAndSave(withSentenceId)
        }
    }

    rate(id: WordId, intervalMs: IntervalMs, rating: Rating.Hard | Rating.Good | Rating.Easy) {

        const userWord = this.getUserWord(id)
        if (!userWord) {
            console.error(`ERROR no user word found for id ${id}. Cannot rate.`)
            return
        }

        // NOTE important ! status could have been changed before and went back in the session to correct
        const withStatusLearn = userWord.withStatus(Status.Learn)

        const newInterval = this.interval.calculateNewInterval(intervalMs, rating)
        const newToLearnNext = this.interval.calculateToLearnNext(newInterval)
        console.log('rating', rating.toUpperCase(), 'current', intervalMs, '*', this.interval.modifier[rating], 'newInterval', newInterval, 'toLearnNext', new Date(newToLearnNext).toLocaleString())
        logInProduction(rating, '-', id)

        const withStatusLearnAndUpdatedToLearnNextAndInterval = withStatusLearn.withToLearnNextAndInterval(newToLearnNext, newInterval)

        // DEVELOPMENT
        // dev: userWord.toLearnNext = Date.now()

        this.addAndSave(withStatusLearnAndUpdatedToLearnNextAndInterval)
    }
}